import { route } from './types'

/**
 * Centralized route definitions for the ConductorOne frontend.
 *
 * All routes should be defined here and accessed via this object.
 * - Static routes: route('/path')
 * - Dynamic routes: route`/path/${param}` (tagged template, auto-encodes params)
 *
 * Note: The object structure is kept flat for ergonomics, but actual URL
 * paths may include prefixes like /admin/.
 *
 * IMPORTANT: Dynamic parameters are automatically URL-encoded via the tagged
 * template syntax. Do NOT pre-encode values passed to route builders.
 *
 * @example
 * import { routes } from 'lib/routes'
 *
 * // Static route
 * <C1Link href={routes.applications.list}>Applications</C1Link>
 *
 * // Dynamic route - NO encoding needed at call site
 * <C1Link href={routes.applications.detail(appId)}>View App</C1Link>
 */
export const routes = {
    // ===== General =====
    home: route('/'),
    login: route('/login'),
    verifyDevice: route('/verify-device'),
    task: (taskId: string) => route`/task/${taskId}`,

    // ===== Dashboard =====
    dashboard: route('/admin/dashboard'),
    analytics: route('/admin/analytics'),
    taskLog: route('/admin/task-log'),
    systemLogViewer: route('/admin/system-log-viewer'),

    // ===== Requests =====
    requests: {
        list: route('/requests'),
        appCatalog: route('/requests/app-catalog'),
        profileCatalog: route('/requests/profile-catalog'),
        myRequests: route('/requests/my-requests'),
        temporary: route('/requests/temporary'),
        accounts: route('/requests/accounts'),
        assignedToMe: route('/requests/assigned-to-me'),
        create: route('/access-requests/create'),
    },

    // ===== Secrets =====
    secrets: {
        list: route('/secrets'),
        create: route('/secrets/create'),
        view: (shareCode: string) => route`/secrets/view/${shareCode}`,
        externalView: (shareCode: string) => route`/ext/secrets/share/${shareCode}`,
    },

    // ===== Assignments =====
    assignments: {
        reviews: {
            list: route('/assignments/reviews'),
            detail: (accessReviewId: string) => route`/assignments/reviews/${accessReviewId}`,
            byUser: (accessReviewId: string) => route`/assignments/reviews/${accessReviewId}/user`,
            user: (accessReviewId: string, userId: string) => route`/assignments/reviews/${accessReviewId}/user/${userId}`,
            byUserDetail: (accessReviewId: string, userId: string) => route`/assignments/reviews/${accessReviewId}/user/${userId}`,
            byApp: (accessReviewId: string) => route`/assignments/reviews/${accessReviewId}/app`,
            byAppDetail: (accessReviewId: string, appId: string) => route`/assignments/reviews/${accessReviewId}/app/${appId}`,
            unstructured: (accessReviewId: string) => route`/assignments/reviews/${accessReviewId}/unstructured`,
        },
    },

    // ===== Applications =====
    applications: {
        list: route('/admin/applications'),
        managed: route('/admin/applications/managed'),
        shadowApps: route('/admin/applications/shadow-apps'),
        unmanagedApps: route('/admin/applications/unmanaged-apps'),
        create: route('/admin/applications/create'),
        createWithType: (appType: string) => route`/admin/applications/create/${appType}`,
        detail: (appId: string) => route`/admin/applications/${appId}`,
        detailTab: (appId: string, tab: string) => route`/admin/applications/${appId}/${tab}`,
        entitlement: (appId: string, entitlementId: string) => route`/admin/applications/${appId}/entitlements/${entitlementId}`,
        entitlementTab: (appId: string, entitlementId: string, tab: string) => route`/admin/applications/${appId}/entitlements/${entitlementId}/${tab}`,
        accounts: (appId: string) => route`/admin/applications/${appId}/accounts`,
        account: (appId: string, appUserId: string) => route`/admin/applications/${appId}/accounts/${appUserId}`,
        accountTab: (appId: string, appUserId: string, tab: string) => route`/admin/applications/${appId}/accounts/${appUserId}/${tab}`,
        groups: (appId: string) => route`/admin/applications/${appId}/groups`,
        roles: (appId: string) => route`/admin/applications/${appId}/roles`,
        resources: (appId: string) => route`/admin/applications/${appId}/resources`,
        resource: (appId: string, resourceTypeId: string, resourceId: string) => route`/admin/applications/${appId}/resources/${resourceTypeId}/${resourceId}`,
        resourceTab: (appId: string, resourceTypeId: string, resourceId: string, tab: string) => route`/admin/applications/${appId}/resources/${resourceTypeId}/${resourceId}/${tab}`,
        resourceGrants: (appId: string, resourceTypeId: string, resourceId: string) => route`/admin/applications/${appId}/resources/${resourceTypeId}/${resourceId}/grants`,
        resourceGroup: (appId: string, resourceTypeId: string, resourceId: string) => route`/admin/applications/${appId}/resources/${resourceTypeId}/${resourceId}/group`,
        resourceGroupTab: (appId: string, resourceTypeId: string, resourceId: string, tab: string) => route`/admin/applications/${appId}/resources/${resourceTypeId}/${resourceId}/group/${tab}`,
        groupTemplates: {
            list: (appId: string) => route`/admin/applications/${appId}/group-templates`,
            create: (appId: string) => route`/admin/applications/${appId}/group-templates/create`,
            detail: (appId: string, actionId: string) => route`/admin/applications/${appId}/group-templates/${actionId}`,
        },
        shadowApp: (appId: string, connectorId: string, resourceTypeId: string, resourceId: string) => route`/admin/applications/shadow-apps/${appId}/${connectorId}/${resourceTypeId}/${resourceId}`,
    },

    // ===== Campaigns =====
    campaigns: {
        list: route('/admin/campaigns'),
        create: route('/admin/campaigns/create'),
        drafts: route('/admin/campaigns/drafts'),
        running: route('/admin/campaigns/running'),
        completed: route('/admin/campaigns/completed'),
        detail: (accessReviewId: string) => route`/admin/campaigns/${accessReviewId}`,
        detailTab: (accessReviewId: string, tab: string) => route`/admin/campaigns/${accessReviewId}/${tab}`,
        selectResources: (accessReviewId: string) => route`/admin/campaigns/${accessReviewId}/select_resources`,
        duplicate: (accessReviewId: string) => route`/admin/campaigns/duplicate/${accessReviewId}`,
        templates: {
            list: route('/admin/campaigns/templates'),
            detail: (recurringId: string) => route`/admin/campaigns/templates/${recurringId}`,
            detailTab: (recurringId: string, tab: string) => route`/admin/campaigns/templates/${recurringId}/${tab}`,
        },
    },

    // ===== Users =====
    users: {
        list: route('/admin/directory/users'),
        detail: (userId: string) => route`/admin/directory/users/${userId}`,
        detailTab: (userId: string, tab: string) => route`/admin/directory/users/${userId}/${tab}`,
    },

    // ===== Directory =====
    directory: {
        groups: {
            list: route('/admin/directory/groups'),
            create: route('/admin/directory/groups/create'),
            detail: (appId: string, entitlementId: string) => route`/admin/directory/groups/${appId}/${entitlementId}`,
            detailTab: (appId: string, entitlementId: string, tab: string) => route`/admin/directory/groups/${appId}/${entitlementId}/${tab}`,
            duplicate: (appId: string, entitlementId: string) => route`/admin/directory/groups/duplicate/${appId}/${entitlementId}`,
        },
    },

    // ===== User Data Sources =====
    userDataSources: {
        list: route('/admin/directory/user-data-sources'),
        tab: (tab: string) => route`/admin/directory/user-data-sources/${tab}`,
        profileTypes: {
            list: route('/admin/directory/user-data-sources/profile-types'),
            create: route('/admin/directory/user-data-sources/profile-types/create'),
            detail: (profileTypeId: string) => route`/admin/directory/user-data-sources/profile-types/${profileTypeId}`,
            detailTab: (profileTypeId: string, tab: string) => route`/admin/directory/user-data-sources/profile-types/${profileTypeId}/${tab}`,
        },
        pushset: {
            list: route('/admin/directory/user-data-sources/pushset'),
            create: route('/admin/directory/user-data-sources/pushset/create'),
            detail: (pushsetId: string) => route`/admin/directory/user-data-sources/pushset/${pushsetId}`,
        },
        pushRule: route('/admin/directory/user-data-sources/push-rule'),
    },

    // ===== Connectors =====
    connectors: {
        list: route('/admin/connectors'),
        create: route('/admin/connectors/create'),
        detail: (catalogId: string, appId: string, connectorId: string) => route`/admin/connector/${catalogId}/${appId}/${connectorId}`,
    },

    // ===== Access Profiles =====
    accessProfiles: {
        list: route('/admin/access-profiles'),
        create: route('/admin/access-profiles/create'),
        detail: (catalogId: string) => route`/admin/access-profiles/${catalogId}`,
        detailTab: (catalogId: string, tab: string) => route`/admin/access-profiles/${catalogId}/${tab}`,
    },

    // ===== Conflict Monitors =====
    conflictMonitors: {
        list: route('/admin/conflict-monitors'),
        create: route('/admin/conflict-monitors/create'),
        detail: (id: string) => route`/admin/conflict-monitors/${id}`,
        detailTab: (id: string, tab: string) => route`/admin/conflict-monitors/${id}/${tab}`,
    },

    // ===== Automations =====
    automations: {
        list: route('/admin/automations'),
        tab: (tab: string) => route`/admin/automations/${tab}`,
        edit: (workflowId: string) => route`/admin/automations/edit/${workflowId}`,
        execution: (executionId: string) => route`/admin/automations/executions/${executionId}`,
    },

    // ===== Offboarding =====
    offboarding: {
        list: route('/admin/offboarding'),
        detail: (taskId: string) => route`/admin/offboarding/${taskId}`,
    },

    // ===== Groups =====
    groups: {
        index: route('/groups'),
        memberOf: route('/groups/member-of'),
        managerOf: route('/groups/manager-of'),
        detail: (appId: string, entitlementId: string) => route`/groups/${appId}/${entitlementId}`,
        detailTab: (appId: string, entitlementId: string, tab: string) => route`/groups/${appId}/${entitlementId}/${tab}`,
    },

    // ===== Explore =====
    explore: {
        inventory: route('/admin/explore/inventory'),
        inventoryTab: (tab: string) => route`/admin/explore/inventory/${tab}`,
        search: route('/admin/explore/search'),
        graph: route('/admin/explore/graph'),
    },

    // ===== Settings =====
    settings: {
        root: route('/admin/settings'),
        tab: (tab: string) => route`/admin/settings/${tab}`,
        apiKeys: route('/admin/settings/api-keys'),
        servicePrincipals: route('/admin/settings/service-principals'),
        workloadFederation: route('/admin/settings/workload-federation'),
        aiConnections: route('/admin/settings/ai-connections'),
        agenticAi: {
            list: route('/admin/settings/agentic-ai'),
            tab: (tab: string) => route`/admin/settings/agentic-ai/${tab}`,
            agent: (id: string) => route`/admin/settings/agentic-ai/agents/${id}`,
            agentTab: (id: string, tab: string) => route`/admin/settings/agentic-ai/agents/${id}/${tab}`,
        },
        tags: route('/admin/settings/tags'),
        branding: route('/admin/settings/branding'),
        externalDataSources: route('/admin/settings/external-data-sources'),
        externalTicketing: {
            list: route('/admin/settings/external-ticketing'),
            detail: (appId: string, connectorId: string, externalTicketingId: string) => route`/admin/settings/external-ticketing/${appId}/${connectorId}/${externalTicketingId}`,
        },
        functions: {
            list: route('/admin/settings/functions'),
            create: route('/admin/settings/functions/create'),
            detail: (id: string) => route`/admin/settings/functions/${id}`,
            detailTab: (id: string, tab: string) => route`/admin/settings/functions/${id}/${tab}`,
        },
        notifications: route('/admin/settings/notifications'),
        notificationPreferences: route('/admin/settings/notification-preferences'),
        organization: route('/admin/settings/organization'),
        passwords: {
            list: route('/admin/settings/passwords'),
            create: route('/admin/settings/passwords/create'),
            detail: (id: string) => route`/admin/settings/passwords/${id}`,
            detailTab: (id: string, tab: string) => route`/admin/settings/passwords/${id}/${tab}`,
            policies: {
                create: route('/admin/settings/passwords/policies/create'),
                detail: (id: string) => route`/admin/settings/passwords/policies/${id}`,
            },
        },
        policies: {
            list: route('/admin/settings/policies'),
            create: route('/admin/settings/policies/create'),
            detail: (policyId: string) => route`/admin/settings/policies/${policyId}`,
            duplicate: (policyId: string) => route`/admin/settings/policies/duplicate/${policyId}`,
        },
        serviceDeskIntegration: {
            list: route('/admin/settings/service-desk-integration'),
            detail: (integrationId: string) => route`/admin/settings/service-desk-integration/${integrationId}`,
        },
        sharedSecrets: {
            list: route('/admin/settings/shared-secrets'),
            tab: (tab: string) => route`/admin/settings/shared-secrets/${tab}`,
        },
        sso: route('/admin/settings/sso'),
        stepUp: route('/admin/settings/step-up'),
        systemLog: route('/admin/settings/system-log'),
        systemManagement: route('/admin/settings/system-management'),
        billingAndUsage: route('/admin/settings/billing-and-usage'),
        vaults: {
            list: route('/admin/settings/vaults'),
            detail: (vaultId: string) => route`/admin/settings/vaults/${vaultId}`,
        },
        webhooks: route('/admin/settings/webhooks'),
    },

    // ===== Profile =====
    profile: {
        index: route('/profile'),
        api: route('/profile/api'),
        vaults: route('/profile/vaults'),
        notifications: route('/profile/notifications'),
    },

    // ===== Conversations =====
    conversations: {
        new: route('/conversations/new'),
        detail: (conversationId: string) => route`/conversations/${conversationId}`,
    },

    // ===== Password Reset =====
    passwordReset: {
        index: route('/password-reset'),
        detail: (id: string) => route`/password-reset/${id}`,
    },

    // ===== Vault Delivery =====
    vaultDelivery: (vaultId: string) => route`/vault-delivery/${vaultId}`,

    // ===== Accounts (Login Flow) =====
    accounts: {
        index: route('/accounts'),
        login: route('/accounts'),
        signup: route('/accounts/signup'),
        signupProviders: {
            oidc: route('/accounts/signup/oidc'),
            okta: route('/accounts/signup/okta'),
            onelogin: route('/accounts/signup/onelogin'),
            pingone: route('/accounts/signup/pingone'),
            jumpcloud: route('/accounts/signup/jumpcloud'),
        },
    },

    // ===== Data Sources =====
    dataSources: {
        detail: (catalogId: string, dataSourceId: string) => route`/admin/data-source/${catalogId}/${dataSourceId}`,
    },

    // ===== Integration =====
    integration: route('/integration'),

    // ===== Sample Pages (Dev Only) =====
    sample: {
        index: route('/sample'),
        components: route('/sample/components'),
        colors: route('/sample/colors'),
        formWithDrawer: route('/sample/form_with_drawer'),
        dynamicForm: route('/sample/dynamic-form'),
        dynamicFormGrouped: route('/sample/dynamic-form-grouped'),
        lucide: route('/sample/lucide'),
    },
} as const

// Re-export type for convenience
export type { Route } from './types'
